import { useState } from "react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Copy, ExternalLink } from "lucide-react";
import { verCertidao, CodeInput } from "@/lib/certidao";
import { DateInput } from "@/components/DateInput";

const pt = (iso) => (iso ? iso.slice(0, 10).split("-").reverse().join("/") : "");
const plus6 = (iso) => { const d = new Date(`${iso.slice(0, 10)}T12:00:00`); d.setMonth(d.getMonth() + 6); return d.toISOString().slice(0, 10); };

function Row({ label, children, testid }) {
  return <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 py-1.5" data-testid={testid}><span className="w-36 text-sm text-slate-500 shrink-0">{label}</span>{children}</div>;
}

// Bloco «Certidão permanente» no processo: código, consulta oficial, data e quem enviou.
export function CertidaoPanel({ pid, doc, onChange, ro }) {
  const [date, setDate] = useState(doc.issue_date || "");
  const code = doc.cert_code;
  const saveCode = async (v) => {
    try { await api.put(`/processes/${pid}/certidao`, { code: v }); toast.success("Código guardado"); onChange(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const saveDate = async (v) => {
    setDate(v);
    if (!v) { return; }
    try { await api.put(`/processes/${pid}/documents/certidao_permanente`, { issue_date: v || null }); onChange(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const copy = () => navigator.clipboard.writeText(code).then(() => toast.success("Código copiado")).catch(() => {});
  const got = doc.status === "recebido";
  const portal = doc.cert_code_by === "cliente" || /\(portal\)/i.test(doc.status_by || "");
  const by = portal ? "Cliente pelo portal" : doc.status_by ? `Agência · ${doc.status_by}` : "Agência";
  const until = date ? plus6(date) : null;
  const expired = until && until < new Date().toISOString().slice(0, 10);
  const cmiOk = !doc.require_valid || (date && !expired);
  return (
    <div className="mt-3 rounded-xl bg-slate-50 border border-slate-100 px-4 py-2.5" data-testid="certidao-panel">
      {code && <Row label="Código" testid="certidao-code-row"><span className="font-mono font-semibold text-slate-800" data-testid="certidao-code">{code}</span>
        <button onClick={copy} className="inline-flex items-center gap-1 text-xs text-slate-600 hover:underline" data-testid="certidao-copy"><Copy size={12} /> Copiar</button></Row>}
      <Row label="Consultar" testid="certidao-consult-row">
        <button onClick={() => verCertidao(doc.consulta_url, code)} data-testid="certidao-ver"
          className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-slate-200 bg-white text-sm font-semibold text-ec-green hover:bg-slate-50">Ver certidão <ExternalLink size={13} /></button>
        {!code && !ro && <CodeInput onSave={saveCode} testid="certidao-team-code" />}
      </Row>
      {got && <Row label="Data da certidão" testid="certidao-date-row">
        <DateInput value={date} disabled={ro} onChange={(e) => saveDate(e.target.value)} className="h-9 w-40" data-testid="certidao-date" />
        {date && <span className={`text-sm ${expired ? "text-amber-700 font-medium" : "text-slate-600"}`} data-testid="certidao-valid-until">{expired ? "caducou a" : "válida até"} {pt(until)}</span>}
      </Row>}
      {got && <Row label="Enviado por" testid="certidao-by"><span className="text-sm text-slate-700">{by}{(doc.cert_code_at || doc.status_at) && ` · ${pt(doc.cert_code_at || doc.status_at)}`}</span></Row>}
      {got && (
        <div className="flex flex-wrap gap-x-4 gap-y-1 pt-2 mt-1 border-t border-slate-200 text-sm" data-testid="certidao-state">
          {cmiOk ? <span className="text-emerald-700" data-testid="certidao-state-cmi">CMI ✓ certidão existe</span>
            : <span className="text-amber-700" data-testid="certidao-state-cmi">CMI · {date ? "certidão com mais de 6 meses" : "falta a data da certidão"}</span>}
          {date && <span className={expired ? "text-amber-700" : "text-slate-700"} data-testid="certidao-state-deed">Escritura · {expired ? "renovar certidão" : `válida até ${pt(until)}`}</span>}
        </div>
      )}
    </div>
  );
}
